/**
 * annexesApi — RTK Query slice cho /purchase/annexes/*.
 * Tag enhanced: 'Annex'. Đồng thời invalidate 'LocalP' / 'ContractAll' nếu cần.
 *
 * Endpoints (Sprint 5):
 *   GET    /purchase/annexes/list?contract_no=&annex_date_from=&annex_date_to=&page=&page_size=
 *   POST   /purchase/annexes/create
 *   GET    /purchase/annexes/{id}
 *   PUT    /purchase/annexes/{id}
 *   DELETE /purchase/annexes/{id}
 *   GET    /purchase/annexes/{id}/print[?cancel=1]
 */
import { baseApi } from './baseApi'
import type {
  AnnexCreateIn,
  AnnexListQuery,
  AnnexOut,
  AnnexUpdateIn,
  DataResponse,
  LocalPPrintResult,
  MessageResponse,
  PageResponse,
} from '@/types/api'

const annexEnhanced = baseApi.enhanceEndpoints({ addTagTypes: ['Annex'] })

export const annexesApi = annexEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listAnnexes: b.query<PageResponse<AnnexOut>, AnnexListQuery | void>({
      query: (params) => ({ url: '/purchase/annexes/list', params: params ?? {} }),
      providesTags: (res, _e, arg) => {
        const contractNo = (arg && (arg as AnnexListQuery).contract_no) || 'ALL'
        const list = res
          ? [
              ...res.items.map((it) => ({ type: 'Annex' as const, id: it.id })),
              { type: 'Annex' as const, id: `LIST-${contractNo}` },
            ]
          : [{ type: 'Annex' as const, id: `LIST-${contractNo}` }]
        return [...list, { type: 'Annex' as const, id: 'LIST' }]
      },
    }),
    getAnnex: b.query<DataResponse<AnnexOut>, number>({
      query: (id) => `/purchase/annexes/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Annex', id }],
    }),
    createAnnex: b.mutation<DataResponse<AnnexOut>, AnnexCreateIn>({
      query: (body) => ({
        url: '/purchase/annexes/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Annex', id: 'LIST' }],
    }),
    updateAnnex: b.mutation<
      DataResponse<AnnexOut>,
      { id: number; body: AnnexUpdateIn }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/annexes/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Annex', id: arg.id },
        { type: 'Annex', id: 'LIST' },
      ],
    }),
    deleteAnnex: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/annexes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Annex', id },
        { type: 'Annex', id: 'LIST' },
      ],
    }),
    /** Print (cancel=true => phụ lục huỷ). Sprint 6 sẽ render PDF thật. */
    printAnnex: b.query<
      DataResponse<LocalPPrintResult>,
      { id: number; cancel?: boolean }
    >({
      query: ({ id, cancel }) => ({
        url: `/purchase/annexes/${id}/print`,
        params: cancel ? { cancel: 1 } : {},
      }),
    }),
  }),
})

export const {
  useListAnnexesQuery,
  useGetAnnexQuery,
  useCreateAnnexMutation,
  useUpdateAnnexMutation,
  useDeleteAnnexMutation,
  useLazyPrintAnnexQuery,
} = annexesApi
